/**
 * Opens a print preview window with the given content
 * Uses Blob URL to create proper document origin for printing
 * 
 * @param {string} title - Title of the print document
 * @param {string} contentHtml - HTML content to print
 * @param {Object} [opts] - Optional settings
 * @param {string} [opts.printerHint] - Friendly printer name (from settings) to display as a Smart Reminder banner
 */
export const openPrintPreview = (title, contentHtml, opts = {}) => {
  const printerHint = (opts && opts.printerHint) ? String(opts.printerHint).trim() : '';
  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title} - Print</title>
      <style>
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 20px;
          color: #1a1a1a;
          line-height: 1.5;
        }
        .print-header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #333;
        }
        .print-header h1 {
          font-size: 24px;
          margin-bottom: 5px;
        }
        .print-header .print-date {
          color: #666;
          font-size: 12px;
        }
        .print-actions {
          text-align: center;
          margin-bottom: 20px;
          padding: 15px;
          background: #f0f0f0;
          border-radius: 8px;
        }
        .print-actions .instruction {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 10px;
        }
        .print-actions .shortcut {
          display: inline-block;
          background: #6366f1;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          font-family: monospace;
          font-size: 16px;
          font-weight: bold;
          margin: 5px;
        }
        .print-actions .hint {
          font-size: 13px;
          color: #666;
          margin-top: 10px;
        }
        .print-actions button {
          padding: 10px 24px;
          font-size: 14px;
          cursor: pointer;
          border: none;
          border-radius: 6px;
          margin-top: 15px;
        }
        .close-btn {
          background: #e5e7eb;
          color: #374151;
        }
        .close-btn:hover {
          background: #d1d5db;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
          font-size: 12px;
        }
        table:not(.pl-table) th, 
        table:not(.pl-table) td {
          border: 1px solid #ddd;
          padding: 8px 10px;
          text-align: left;
        }
        table:not(.pl-table) th {
          background: #f3f4f6;
          font-weight: 600;
        }
        /* P&L specific table styles - no borders */
        .pl-table td {
          border: none;
        }
        .text-right {
          text-align: right;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
          margin: 20px 0;
        }
        .summary-card {
          border: 1px solid #ddd;
          padding: 15px;
          border-radius: 8px;
        }
        .summary-card.green { background: #f0fdf4; border-color: #86efac; }
        .summary-card.red { background: #fef2f2; border-color: #fca5a5; }
        .summary-card.blue { background: #eff6ff; border-color: #93c5fd; }
        .summary-card.yellow { background: #fefce8; border-color: #fde047; }
        .summary-label {
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }
        .summary-value {
          font-size: 20px;
          font-weight: 700;
        }
        .summary-value.green { color: #16a34a; }
        .summary-value.red { color: #dc2626; }
        .summary-value.blue { color: #2563eb; }
        .section-title {
          font-size: 16px;
          font-weight: 600;
          margin: 20px 0 10px;
          padding-bottom: 5px;
          border-bottom: 1px solid #ddd;
        }
        .totals-row {
          background: #f9fafb;
          font-weight: 600;
        }
        @media print {
          .print-actions {
            display: none !important;
          }
          body {
            padding: 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <h1>${title}</h1>
        <div class="print-date">Printed: ${new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
      </div>
      
      <div class="print-actions">
        ${printerHint ? `
          <div style="margin-bottom: 15px; padding: 12px 16px; background: #fef3c7; border: 2px solid #f59e0b; border-radius: 8px; display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 16px; color: #78350f;">
            <span style="font-size: 22px;">🖨️</span>
            <span><strong>Send this to:</strong> <span style="font-weight: 700;">${printerHint}</span></span>
          </div>
        ` : ''}
        <div class="instruction">To print this report, press:</div>
        <span class="shortcut">Ctrl + P</span> <span style="color: #666;">or</span> <span class="shortcut">⌘ + P</span>
        <div class="hint">The print dialog will open. Select your printer or "Save as PDF".</div>
        <br>
        <button class="close-btn" onclick="window.close()">Close Window</button>
      </div>
      
      ${contentHtml}
    </body>
    </html>
  `;
  
  // Use Blob URL to create a proper document origin
  const blob = new Blob([printContent], { type: 'text/html' });
  const blobUrl = URL.createObjectURL(blob);
  
  const printWindow = window.open(blobUrl, '_blank', 'width=900,height=700');
  
  if (!printWindow) {
    alert('Please allow pop-ups to use the print feature, then try again.');
    URL.revokeObjectURL(blobUrl);
    return;
  }
  
  // Clean up the blob URL after the window loads
  printWindow.onload = function() {
    URL.revokeObjectURL(blobUrl);
  };
  
  printWindow.focus();
};
